import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { ArrowLeft, Copy, Crown, Mail, Pencil, Shield, UserMinus } from 'lucide-react';
import { functions } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
import { isIosShell } from '../lib/nativeBridge';
import { Badge, Button, Input, Modal, Spinner, cx } from './ui';
import OrgBilling from './OrgBilling';

type Role = 'owner' | 'admin' | 'member';

interface Overview {
  ok: true;
  viewerRole: Role;
  org: {
    code: string;
    name: string;
    organisation: string;
    ownerUid: string | null;
    active: boolean;
    comped: boolean;
    createdAt: string | null;
    status: 'comped' | 'paid' | 'trial' | 'lapsed' | 'suspended';
    trialEndsAt: string | null;
    paidUntil: string | null;
  };
  price: { monthlySgd: number; yearlySgd: number };
  trial: { credits: number; speechSeconds: number };
  pool: { key: string; credits: number; creditLimit: number; speechSeconds: number; speechLimit: number };
  limits: { members: number; creditsPerMonth: number; speechSecondsPerMonth: number; storageBytes: number };
  members: { uid: string; email: string; displayName: string; role: Role; joinedAt: string | null; lastSeenAt: string | null }[];
  projects: { id: string; name: string; ownerUid: string; ownerName: string; memberCount: number; updatedAt: string | null }[];
  usage: {
    since: string;
    perMember: { uid: string; credits: number; reads: number; speechSeconds: number; storageBytes: number }[];
    credits: number;
    reads: number;
    speechSeconds: number;
    storageBytes: number;
  };
}

const call = <I, O = { ok: true }>(name: string) => httpsCallable<I, O>(functions, name);
const overview = call<void, Overview>('orgOverview');
const setRole = call<{ uid: string; role: 'admin' | 'member' }>('setOrgMemberRole');
const removeMember = call<{ uid: string }>('removeOrgMember');
const transfer = call<{ uid: string }>('transferOrgOwnership');
const rename = call<{ name: string; organisation: string }>('renameOrganisation');
const invite = call<{ email: string }, { ok: true; sent: boolean; reason: string | null }>('inviteToLab');

type Tab = 'members' | 'projects' | 'usage' | 'billing';

/**
 * The Owner dashboard: who is in the organisation, what it is working on, what
 * it has used this month, and (soon) its plan. For the owner and admins.
 */
export default function OrgDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Coming back from HitPay lands on the Billing tab.
  const [tab, setTab] = useState<Tab>(() =>
    /[?&](paid|subscribed)=/.test(window.location.search) ? 'billing' : 'members',
  );
  const iosApp = isIosShell();

  const load = useCallback(async () => {
    try {
      setData((await overview()).data);
      setError(null);
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not load the organisation.');
    }
  }, []);
  useEffect(() => void load(), [load]);

  if (error && !data) {
    return (
      <div className="mx-auto max-w-2xl">
        <BackLink />
        <p className="rounded-lg border border-red-800 bg-red-950 p-3 text-sm text-red-200">{error}</p>
      </div>
    );
  }
  if (!data || !user) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-6" />
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'members', label: `Members (${data.members.length})` },
    { id: 'projects', label: `Projects (${data.projects.length})` },
    { id: 'usage', label: 'Usage' },
    // Inside the App Store build nothing about paying is shown (guideline 3.1.1).
    ...(iosApp ? [] : [{ id: 'billing' as Tab, label: 'Billing' }]),
  ];

  return (
    <div className="mx-auto max-w-4xl">
      <BackLink />
      <OrgHeader data={data} onChanged={load} />

      <div className="mb-4 flex gap-1 overflow-x-auto border-b border-ink-800">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cx(
              'shrink-0 border-b-2 px-3 py-2 text-sm transition-colors pointer-coarse:min-h-11',
              tab === t.id ? 'border-accent-400 text-ink-100' : 'border-transparent text-ink-400 hover:text-ink-200',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && <p className="mb-3 text-sm text-red-300">{error}</p>}
      {tab === 'members' && <Members data={data} myUid={user.uid} onChanged={load} onError={setError} />}
      {tab === 'projects' && <Projects data={data} />}
      {tab === 'usage' && <Usage data={data} />}
      {tab === 'billing' && !iosApp && <OrgBilling onChanged={load} />}
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/"
      className="mb-2 inline-flex items-center gap-1 text-sm text-ink-400 hover:text-ink-100 pointer-coarse:min-h-11"
    >
      <ArrowLeft size={14} />
      Projects
    </Link>
  );
}

function OrgHeader({ data, onChanged }: { data: Overview; onChanged: () => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(data.org.name);
  const [org, setOrg] = useState(data.org.organisation);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const pretty = `${data.org.code.slice(0, 4)} ${data.org.code.slice(4)}`;

  return (
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          {data.org.name || 'Organisation'}
          {data.viewerRole === 'owner' && (
            <button
              className="icon-btn text-ink-400 hover:bg-ink-800 hover:text-ink-100"
              aria-label="Rename"
              onClick={() => setEditing(true)}
            >
              <Pencil size={14} />
            </button>
          )}
        </h1>
        {data.org.organisation && <p className="text-sm text-ink-400">{data.org.organisation}</p>}
      </div>
      <div className="rounded-lg border border-ink-700 bg-ink-800/40 px-3 py-2 text-right">
        <p className="text-xs text-ink-400">Join code</p>
        <button
          className="flex items-center gap-2 font-mono text-lg tracking-wider text-accent-400"
          onClick={() => {
            void navigator.clipboard?.writeText(data.org.code).then(() => {
              setCopied(true);
              window.setTimeout(() => setCopied(false), 1500);
            });
          }}
          title="Copy"
        >
          {pretty}
          <Copy size={14} className="text-ink-400" />
        </button>
        {copied && <p className="text-xs text-emerald-300">Copied</p>}
      </div>

      <Modal open={editing} onClose={() => setEditing(false)} title="Rename organisation">
        <div className="space-y-3">
          <label className="block">
            <span className="field-label">Name</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="block">
            <span className="field-label">Organisation (optional)</span>
            <Input value={org} onChange={(e) => setOrg(e.target.value)} placeholder="Optional" />
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={saving}
              onClick={async () => {
                setSaving(true);
                try {
                  await rename({ name, organisation: org });
                  setEditing(false);
                  onChanged();
                } finally {
                  setSaving(false);
                }
              }}
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function Members({
  data,
  myUid,
  onChanged,
  onError,
}: {
  data: Overview;
  myUid: string;
  onChanged: () => void;
  onError: (m: string | null) => void;
}) {
  const [email, setEmail] = useState('');
  const [inviting, setInviting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const isOwner = data.viewerRole === 'owner';
  const full = data.members.length >= data.limits.members;

  async function act(uid: string, fn: () => Promise<unknown>) {
    setBusy(uid);
    onError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      onError((e as Error)?.message ?? 'That did not work.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <div className="mb-4 rounded-xl border border-ink-700 bg-ink-800/40 p-4">
        <p className="mb-1 text-sm font-medium">Invite someone</p>
        <p className="mb-3 text-xs text-ink-400">
          They get an email with the join code. After signing in, they choose “I have a lab code” and enter it.
          {full && ` Your plan includes ${data.limits.members} members; extra members will be an add-on.`}
        </p>
        <div className="flex gap-2">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@example.com"
          />
          <Button
            variant="primary"
            icon={<Mail size={16} />}
            loading={inviting}
            disabled={!email.trim()}
            onClick={async () => {
              setInviting(true);
              setNotice(null);
              try {
                const r = (await invite({ email: email.trim() })).data;
                setNotice(r.sent ? `Invitation sent to ${email.trim()}.` : `Could not send the email — share the code ${data.org.code} with them instead.`);
                if (r.sent) setEmail('');
              } catch (e) {
                setNotice((e as Error)?.message ?? 'Could not send the invitation.');
              } finally {
                setInviting(false);
              }
            }}
          >
            Invite
          </Button>
        </div>
        {notice && <p className="mt-2 text-xs text-ink-300">{notice}</p>}
      </div>

      <ul className="divide-y divide-ink-800 rounded-xl border border-ink-700">
        {data.members.map((m) => {
          const me = m.uid === myUid;
          const canRemove = !me && m.role !== 'owner' && (isOwner || m.role === 'member');
          return (
            <li key={m.uid} className="flex flex-wrap items-center gap-2 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">
                  {m.displayName || m.email}
                  {me && <span className="text-ink-400"> (you)</span>}
                </p>
                <p className="truncate text-xs text-ink-400">
                  {m.email}
                  {m.lastSeenAt && ` · last seen ${new Date(m.lastSeenAt).toLocaleDateString()}`}
                </p>
              </div>
              <RoleBadge role={m.role} />
              {isOwner && !me && m.role !== 'owner' && (
                <Button
                  variant="ghost"
                  loading={busy === m.uid}
                  onClick={() =>
                    act(m.uid, () => setRole({ uid: m.uid, role: m.role === 'admin' ? 'member' : 'admin' }))
                  }
                >
                  {m.role === 'admin' ? 'Make member' : 'Make admin'}
                </Button>
              )}
              {isOwner && !me && (
                <Button
                  variant="ghost"
                  icon={<Crown size={14} />}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Make ${m.displayName || m.email} the owner? You will become an admin and can no longer manage billing.`,
                      )
                    ) {
                      void act(m.uid, () => transfer({ uid: m.uid }));
                    }
                  }}
                >
                  Make owner
                </Button>
              )}
              {canRemove && (
                <Button
                  variant="ghost"
                  icon={<UserMinus size={14} />}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Remove ${m.displayName || m.email} from the organisation? Their projects stay where they are.`,
                      )
                    ) {
                      void act(m.uid, () => removeMember({ uid: m.uid }));
                    }
                  }}
                >
                  Remove
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function RoleBadge({ role }: { role: Role }) {
  if (role === 'owner')
    return (
      <Badge tone="good">
        <Crown size={10} />
        owner
      </Badge>
    );
  if (role === 'admin')
    return (
      <Badge tone="info">
        <Shield size={10} />
        admin
      </Badge>
    );
  return <Badge tone="neutral">member</Badge>;
}

function Projects({ data }: { data: Overview }) {
  if (!data.projects.length) {
    return <p className="text-sm text-ink-400">No projects in this organisation yet.</p>;
  }
  return (
    <ul className="divide-y divide-ink-800 rounded-xl border border-ink-700">
      {data.projects.map((p) => (
        <li key={p.id} className="flex flex-wrap items-center gap-2 px-3 py-2.5">
          <Link to={`/p/${p.id}`} className="min-w-0 flex-1 truncate text-sm hover:underline">
            {p.name || 'Untitled'}
          </Link>
          <span className="text-xs text-ink-400">
            {p.ownerName && `${p.ownerName} · `}
            {p.memberCount} {p.memberCount === 1 ? 'person' : 'people'}
            {p.updatedAt && ` · updated ${new Date(p.updatedAt).toLocaleDateString()}`}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Usage({ data }: { data: Overview }) {
  const u = data.usage;
  const L = data.limits;
  const P = data.pool;
  const month = new Date(u.since).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const names = new Map(data.members.map((m) => [m.uid, m.displayName || m.email]));
  const trial = P.key === 'trial';
  return (
    <div>
      <p className="mb-3 text-sm text-ink-400">
        {data.org.comped
          ? `${month}. This organisation is not charged; the Lab plan's allowances are shown for reference.`
          : trial
            ? 'Free trial: the allowance below covers the whole trial. Subscribing gives the full monthly allowance straight away.'
            : `${month}. Once the organisation's allowance is used, members' own top-up credits are used.`}
      </p>
      <div className="mb-6 grid gap-3 sm:grid-cols-2">
        <Meter
          label={trial ? 'AI credits (trial)' : 'AI credits'}
          used={P.credits}
          limit={P.creditLimit}
          fmt={(n) => `${n}`}
          note={`${u.reads} automatic reads this month`}
        />
        <Meter label={trial ? 'Spoken notes (trial)' : 'Spoken notes'} used={P.speechSeconds} limit={P.speechLimit} fmt={minutes} />
        <Meter label="Storage" used={u.storageBytes} limit={L.storageBytes} fmt={bytes} />
        <Meter label="Members" used={data.members.length} limit={L.members} fmt={(n) => `${n}`} />
      </div>
      <p className="field-label mb-2">By member, this month</p>
      <div className="overflow-x-auto rounded-xl border border-ink-700">
        <table className="w-full text-sm">
          <thead className="text-left text-xs text-ink-400">
            <tr>
              <th className="px-3 py-2 font-medium">Member</th>
              <th className="px-3 py-2 text-right font-medium">Credits</th>
              <th className="px-3 py-2 text-right font-medium">Spoken</th>
              <th className="px-3 py-2 text-right font-medium">Storage</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-800">
            {u.perMember.map((m) => (
              <tr key={m.uid}>
                <td className="px-3 py-2">{names.get(m.uid) ?? m.uid}</td>
                <td className="px-3 py-2 text-right tabular-nums">{m.credits}</td>
                <td className="px-3 py-2 text-right tabular-nums">{minutes(m.speechSeconds)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{bytes(m.storageBytes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Meter({
  label,
  used,
  limit,
  fmt,
  note,
}: {
  label: string;
  used: number;
  limit: number;
  fmt: (n: number) => string;
  note?: string;
}) {
  const pct = Math.min(100, Math.round((used / limit) * 100));
  return (
    <div className="rounded-xl border border-ink-700 bg-ink-800/40 p-4">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs tabular-nums text-ink-400">
          {fmt(used)} of {fmt(limit)}
        </p>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-ink-800">
        <div
          className={cx('h-full', pct >= 90 ? 'bg-red-400' : pct >= 70 ? 'bg-amber-400' : 'bg-emerald-400')}
          style={{ width: `${pct}%` }}
        />
      </div>
      {note && <p className="mt-1.5 text-xs text-ink-400">{note}</p>}
    </div>
  );
}

function minutes(seconds: number): string {
  if (seconds >= 3600) return `${(seconds / 3600).toFixed(1)} h`;
  return `${Math.round(seconds / 60)} min`;
}

function bytes(n: number): string {
  if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
  if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.round(n / 1024)} KB`;
}
